import { token } from '../theme/tokens.js';

/**
 * The control recipes both apps share: the button family and the text field.
 *
 * These were copied into a dozen components across `apps/web` and `apps/admin`
 * before landing here — `spaceControls.ts`'s header said a module spanning
 * surfaces would be this package's job, and `MembersTab` said to promote the
 * string "if an eighth caller ever wants it". The values are unchanged; what
 * changed is that there is one of each.
 *
 * `min-h-9` is 36px, the floor for a control someone has to hit reliably. These
 * were `px-2 py-1 text-xs` once — a target around 24px tall, sitting beside a
 * destructive act.
 */
export const BUTTON_CLASS =
  'inline-flex min-h-9 items-center justify-center whitespace-nowrap border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60';

/**
 * The same button for a label that is a **sentence** rather than a word.
 *
 * A second class, not `BUTTON_CLASS` plus an override: Tailwind decides between
 * two utilities of one group by their order in the generated stylesheet, never
 * by their order in a `class` attribute, so appending `whitespace-normal` to a
 * string already carrying `whitespace-nowrap` silently does nothing. Left-aligned
 * because two centred lines read as a heading rather than as a control.
 */
export const WRAPPING_BUTTON_CLASS =
  'inline-flex min-h-9 items-center justify-start whitespace-normal border px-3 py-1.5 text-left text-sm font-medium transition-colors disabled:opacity-60';

export function neutralButtonStyle() {
  return {
    borderColor: token('bp-border-strong'),
    color: token('bp-text'),
    borderRadius: token('radius-md'),
  };
}

/**
 * The design system's signature cue: the blue glow is reserved for the primary
 * act.
 *
 * The label is `bp-on-accent` (white), not `bp-blue-text` — the readme's own
 * line for solid blue buttons. `bp-blue-text` reads fine on the navy app
 * background it was built for; against this fill it lands under 4:1, short of
 * WCAG AA's 4.5:1 for text this size.
 */
export function primaryButtonStyle() {
  return {
    borderColor: token('bp-blue'),
    background: token('bp-blue-deep'),
    color: token('bp-on-accent'),
    borderRadius: token('radius-md'),
  };
}

/** The one destructive treatment: the neutral button wearing the danger colour. */
export function dangerButtonStyle() {
  return { ...neutralButtonStyle(), color: token('bp-danger') };
}

export function fieldStyle() {
  return {
    background: token('bp-input'),
    borderColor: token('bp-border-strong'),
    borderRadius: token('radius-md'),
    color: token('bp-text'),
  };
}
